import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import CasoStudio from "@/components/site/CasoStudio";
import MobileAccordion from "@/components/site/MobileAccordion";
import { openCallReason } from "@/lib/site";

const CONTROL_POINTS = [
  { n: "01", t: "Gli account restano tuoi", d: "Booking e Airbnb sono intestati a te, non a me. Se un giorno ci lasciamo, porti via tutto: profili, storico, contatti." },
  { n: "02", t: "Le recensioni restano tue", d: "Ogni recensione guadagnata finisce sul tuo profilo e ci resta per sempre. È patrimonio tuo, non di chi ti segue." },
  { n: "03", t: "Una sola app per tutto il sistema", d: "Con l'app che tiene allineati i calendari — inclusa, la pago io — vedi prezzi, calendari e prenotazioni in un colpo d'occhio, dal telefono. Lo stesso strumento delle grandi strutture, senza pagarlo tu." },
  { n: "04", t: "Io lavoro dentro, tu vedi tutto", d: "La gestione giornaliera la facciamo insieme: io aggiusto prezzi e calendario ogni giorno, tu apri l'app e controlli ogni mossa. Niente scatole nere." },
];

function PhoneDemo() {
  const [videoOk, setVideoOk] = useState(true);
  return (
    <div className="mx-auto w-[170px] shrink-0 sm:mx-0 sm:w-[190px]" data-testid="app-demo-frame">
      <div className="rounded-[2rem] border border-[#4D2C20] bg-[#0C0A08] p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
        <div className="relative aspect-[9/19] overflow-hidden rounded-[1.6rem]">
          {videoOk ? (
            <video
              data-testid="app-demo-video"
              autoPlay
              muted
              loop
              playsInline
              onError={() => setVideoOk(false)}
              className="h-full w-full object-cover"
            >
              <source src="/videos/channel-manager.webm" type="video/webm" />
              <source src="/videos/channel-manager.mp4" type="video/mp4" />
            </video>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 border border-dashed border-[#4D2C20] px-4 text-center">
              <span className="font-heading text-2xl font-black tracking-tighter text-[#E05A2C]">YU</span>
              <span className="font-mono text-xs uppercase tracking-widest text-[#A89F91]">Video dell'app in arrivo</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TEAM = [
  {
    name: "Luciano Di Blasi",
    role: "Founder · Revenue Manager",
    bio: "Prezzi, calendario, annunci e report: sono io che lavoro dentro la tua struttura, ogni giorno. Quando scrivi, rispondo io — niente ticket, niente attese.",
    photo: "/images/luciano.jpg",
    page: "/revenue-manager-case-vacanze-sicilia",
  },
  {
    name: "Valerio Pavano",
    role: "Commercialista",
    bio: "Partita IVA, tasse, scartoffie: ci pensa lui. Tu non vedi nemmeno la burocrazia — sai solo che è fatta bene e in tempo.",
    photo: "/images/valerio.jpg",
    page: "/commercialista-affitti-brevi-sicilia",
  },
  {
    name: "Massimiliano Masellis",
    role: "Architetto · Attività ricettive",
    bio: "Progetta spazi pensati per ospitare, non solo per piacere. Se devi ristrutturare o aprire da zero, la tua struttura nasce già pronta a vendere.",
    photo: "/images/massimiliano.jpg",
    page: "/aprire-struttura-ricettiva-sicilia",
  },
  {
    name: "Giovanni Costagliola",
    role: "Fotografo",
    bio: "Le foto giuste vendono le notti, quelle sbagliate le regalano ai concorrenti. Le tue, da oggi, sono quelle giuste.",
    photo: "/images/giovanni.jpg",
    page: "/fotografo-strutture-ricettive-sicilia",
  },
];

export default function ChiSono() {
  const [openIdx, setOpenIdx] = useState(null);
  const [acc, setAcc] = useState(null);
  return (
    <section id="chi-sono" data-testid="chi-sono-section" className="border-b border-[#2A241F] py-12 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="section-kicker">Chi ti segue</p>
        </Reveal>

        <div className="mt-12 grid gap-12 lg:grid-cols-[2fr_3fr] lg:gap-20">
          <Reveal delay={0.1}>
            <div data-testid="luciano-photo-frame" className="relative aspect-[4/5] overflow-hidden border border-[#383029] bg-[#1C1815]">
              <img
                src="/images/luciano.jpg"
                alt="Luciano, fondatore di YU YieldUp"
                className="h-full w-full object-cover grayscale contrast-110"
              />
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <h2 className="font-heading text-3xl font-black uppercase leading-[1.05] tracking-tight text-[#FAF7F2] sm:text-4xl lg:text-5xl">
              Piacere, sono Luciano.
            </h2>

            <div className="mt-8 space-y-5 text-base font-light leading-relaxed text-[#C4BBB0] sm:text-lg">
              <p>
                Faccio l'host da dieci anni con la mia società, <span className="font-medium text-[#FAF7F2]">OpenSicily Homes</span>: non ti parlo del tuo lavoro dall'esterno, è anche il mio. YU YieldUp nasce per metterti in mano gli stessi strumenti e lo stesso metodo dei grandi hotel, adattati alla tua struttura.
              </p>
              <p className="hidden border-l-2 border-[#E05A2C] pl-6 sm:block">
                Chi entra in YieldUp ha me come punto di riferimento diretto: quando scrivi, rispondo io. Seguo personalmente un numero massimo di strutture alla volta.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link
                to="/revenue-manager-case-vacanze-sicilia"
                data-testid="chi-sono-luciano-page-link"
                className="btn-glow inline-block rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
              >
                Scopri cosa faccio, in pratica →
              </Link>
              <button
                data-testid="chi-sono-cta"
                onClick={() => openCallReason("chi-sono")}
                className="min-h-[44px] text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#FAF7F2] hover:decoration-[#E05A2C]"
              >
                Parliamone — 30 minuti gratis
              </button>
            </div>
          </Reveal>
        </div>

        <MobileAccordion id="caso" title="Un caso concreto: OpenSicily Homes" open={acc === "caso"} onToggle={() => setAcc(acc === "caso" ? null : "caso")}>
        <Reveal delay={0.1}>
          <div className="mt-2 sm:mt-16">
            <CasoStudio />
          </div>
        </Reveal>
        </MobileAccordion>

        <MobileAccordion id="team" title="Il team di YieldUp" open={acc === "team"} onToggle={() => setAcc(acc === "team" ? null : "team")}>
        <Reveal delay={0.1}>
          <h3 className="mt-2 font-heading text-xl font-black uppercase tracking-tight text-[#FAF7F2] sm:mt-20 sm:text-2xl">
            Il team di YieldUp: parla con noi, 30 minuti gratis
          </h3>
          <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-[#C4BBB0]">
            Quattro professionisti, quattro consulenze separate, una squadra sola. Ognuno fa una cosa e la fa bene, e quando serve fa riferimento agli altri: per il bene della tua struttura, non per venderti un pacchetto. Entriamo nel tuo progetto solo se possiamo esserti utili — altrimenti te lo diciamo subito.
          </p>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 items-start gap-3 sm:mt-10 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {TEAM.map((person, i) => {
            const open = openIdx === i;
            return (
              <Reveal key={person.name} delay={0.1 + i * 0.08}>
                <div data-testid={`founder-card-${i + 1}`} className={`border transition-colors duration-300 ${open ? "border-[#E05A2C]" : "border-[#2A241F] hover:border-[#E05A2C]"}`}>
                  <button
                    data-testid={`founder-toggle-${i + 1}`}
                    onClick={() => setOpenIdx(open ? null : i)}
                    aria-expanded={open}
                    className="group flex w-full flex-col text-left"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden sm:aspect-[4/5]">
                      <img
                        src={person.photo}
                        alt={person.name}
                        loading="lazy"
                        className="h-full w-full object-cover grayscale contrast-110 transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                    <span className="flex w-full items-center justify-between gap-2 p-4 sm:gap-4 sm:p-7">
                      <span className="min-w-0">
                        <span className="block font-heading text-base font-black uppercase tracking-tight text-[#FAF7F2] transition-colors duration-300 group-hover:text-[#E05A2C] sm:text-xl">{person.name}</span>
                        <span className="mt-1 block font-mono text-[11px] uppercase tracking-widest text-[#E05A2C] sm:text-xs">{person.role}</span>
                      </span>
                      {open ? (
                        <Minus className="h-5 w-5 shrink-0 text-[#E05A2C]" />
                      ) : (
                        <Plus className="h-5 w-5 shrink-0 text-[#A89F91] transition-colors duration-300 group-hover:text-[#E05A2C]" />
                      )}
                    </span>
                  </button>
                  <div className="border-t border-[#2A241F] p-3 sm:p-4">
                    <a
                      data-testid={`founder-page-link-${i + 1}`}
                      href={person.page}
                      className="flex min-h-[44px] items-center justify-center rounded-full border border-[#E05A2C] px-3 py-2 text-center text-xs font-medium text-[#E05A2C] transition-colors duration-300 hover:bg-[#E05A2C] hover:text-white sm:px-5 sm:py-3 sm:text-sm"
                    >
                      Scopri la consulenza →
                    </a>
                  </div>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div data-testid={`founder-content-${i + 1}`} className="border-t border-[#2A241F] px-6 pb-7 pt-6 sm:px-7">
                          <p className="text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">{person.bio}</p>
                          <button
                            data-testid={`founder-book-${i + 1}`}
                            onClick={() => openCallReason("team")}
                            className="btn-glow mt-6 inline-block rounded-full bg-[#E05A2C] px-6 py-3 text-xs font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98] sm:text-sm"
                          >
                            Prenota la chiamata con {person.name.split(" ")[0]}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
        </MobileAccordion>

        <MobileAccordion id="controllo" title="Il controllo resta a te" open={acc === "controllo"} onToggle={() => setAcc(acc === "controllo" ? null : "controllo")}>
        <Reveal delay={0.12}>
          <div data-testid="channel-manager-strip" className="mt-2 border sm:mt-20 border-[#4D2C20] bg-[#2B1B15] p-6 sm:p-10">
            <p className="section-kicker">La differenza più importante</p>
            <p className="mt-4 font-heading text-xl font-black uppercase leading-tight tracking-tight text-[#FAF7F2] sm:text-2xl">
              Il controllo resta a te. Sempre.
            </p>
            <div className="mt-8 space-y-6">
              {CONTROL_POINTS.map((item) => (
                <div key={item.n} data-testid={`control-point-${item.n}`} className={`${item.n === "01" ? "flex" : "hidden sm:flex"} gap-5 border-t border-[#4D2C20] pt-6`}>
                  <div className={`flex-1 ${item.n === "03" ? "flex flex-col gap-6 sm:flex-row sm:items-start" : ""}`}>
                    <div className="flex-1">
                      <p className="font-heading text-base font-black uppercase tracking-tight text-[#FAF7F2] sm:text-lg">{item.t}</p>
                      <p className="mt-2 max-w-2xl text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
                        {item.n === "01" ? <><span className="sm:hidden">Booking e Airbnb sono intestati a te. Anche le recensioni: se un giorno ci lasciamo, porti via tutto.</span><span className="hidden sm:inline">{item.d}</span></> : item.d}
                      </p>
                    </div>
                    {item.n === "03" && <PhoneDemo />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        </MobileAccordion>

        <Reveal delay={0.15}>
          <div data-testid="testimonials-strip" className="mt-20 hidden border-t border-[#2A241F] pt-14 sm:block">
            <p className="section-kicker">Chi resta</p>
            <h3 className="mt-4 max-w-3xl font-heading text-2xl font-black uppercase leading-[1.05] tracking-tight text-[#FAF7F2] sm:text-3xl">
              I clienti restano, anno dopo anno.
            </h3>
            <p className="mt-6 max-w-2xl text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
              Alcuni dei proprietari che seguo sono con me da tanti anni. Nessun vincolo contrattuale li trattiene: restano perché conviene.
            </p>
            <div data-testid="testimonials-scroll" className="mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4">
              {[
                {
                  q: "Prima mi arrivava un bonifico a fine mese e basta: scoprivo com'era andata sempre dopo. Ora so esattamente quanto ho guadagnato e perché. E quando scrivo, risponde lui.",
                  who: "Corrado P. — Villa con piscina, zona Siracusa · con YU da 3 anni",
                },
                {
                  q: "Di agenzie ne avevo già cambiate due, ero scettica. La differenza? I prezzi si muovono davvero, ogni settimana, e me li spiega in parole che capisco anch'io.",
                  who: "Floriana N. — B&B a Ortigia · con YU da 2 anni",
                },
                {
                  q: "Apro la mia dashboard e vedo tutto: prenotazioni, prezzi, cosa sta facendo Luciano e perché. Non devo chiedere niente a nessuno: il controllo ce l'ho io.",
                  who: "Sebastiano I. — Appartamenti, Catania · con YU da 2 anni",
                },
                {
                  q: "Si vede la fatica che fa per trovare il prezzo giusto: non svende mai per riempire, e quando alza mi spiega il motivo. I numeri di quest'anno parlano da soli.",
                  who: "Simona C. — Country lodge, Noto · con YU da 4 anni",
                },
                {
                  q: "Gli account sono miei, le recensioni sono mie. Lui ci lavora dentro ogni giorno e io controllo ogni mossa dal telefono.",
                  who: "Alessandro N. — Residence sul mare, Avola · con YU da 1 anno",
                },
              ].map((t, i) => (
                <figure key={t.who} data-testid={`testimonial-${i + 1}`} className="w-[280px] shrink-0 snap-start border-l-2 border-[#E05A2C] bg-[#16110E] p-6 sm:w-[340px] sm:p-7">
                  <blockquote className="text-sm font-light italic leading-relaxed text-[#C4BBB0] sm:text-base">
                    “{t.q}”
                  </blockquote>
                  <figcaption className="mt-4 font-mono text-xs uppercase tracking-widest text-[#A89F91]">{t.who}</figcaption>
                </figure>
              ))}
            </div>
            <p className="mt-2 font-mono text-xs uppercase tracking-widest text-[#A89F91]">Scorri per leggere le altre →</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
